<template>
  <div class="pb-8">
    <div class="flex flex-col md:flex-row md:relative items-center mb-6 gap-2">
      <h1 class="text-[28px] font-semibold text-grey-light-900 md:mr-auto">Stores</h1>
      <div class="md:absolute md:left-1/2 md:-translate-x-1/2">
        <ProductToggle />
      </div>
    </div>

    <hr class="border-grey-light-200 mb-6" />

    <DesktopFilterBar />
    <MobileFilterBar />

    <div class="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
      <SidebarFilters />

      <main class="min-w-0">
        <div v-if="error" class="flex flex-col items-center justify-center py-[200px] gap-2.5">
          <p class="font-semibold text-2xl text-grey-light-400 text-center">Oops.. Something went wrong</p>
          <p class="text-sm text-grey-light-400 text-center">Please refresh your browser to try again</p>
        </div>

        <div v-else-if="loading" class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          <div v-for="n in 9" :key="n">
            <div class="rounded-2xl overflow-hidden aspect-4/3 bg-grey-light-200">
              <Skeleton class="w-full h-full" />
            </div>
            <Skeleton class="h-5 w-32 mt-3 ml-4 rounded-md" />
          </div>
        </div>

        <div v-else-if="shops.length > 0">
          <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <StoreCard v-for="store in visibleShops" :key="store.id" :store="store" @openStore="openStore" />
          </div>
          <div class="flex justify-center mt-8">
            <button
              v-if="shops.length > visibleCount"
              class="px-6 py-2.5 rounded-full border border-grey-light-300 text-sm font-semibold text-grey-light-700 hover:bg-grey-light-100 transition"
              @click="visibleCount += PAGE_SIZE"
            >
              See more
            </button>
            <button
              v-else-if="visibleCount > PAGE_SIZE"
              class="px-6 py-2.5 rounded-full border border-grey-light-300 text-sm font-semibold text-grey-light-700 hover:bg-grey-light-100 transition"
              @click="visibleCount = PAGE_SIZE"
            >
              Show less
            </button>
          </div>
        </div>

        <div v-else class="flex flex-col justify-center items-center h-[250px] text-grey-light-600 text-center">
          <h2 class="text-2xl">Nothing here yet — but not for long!</h2>
          <p>We're adding new stores all the time. Check back soon to see the latest additions.</p>
        </div>
      </main>
    </div>

    <BoostStoreModal v-model:open="isDialogOpen" :storeId="selectedStoreId" />
  </div>
</template>

<script setup lang="ts">
import { Skeleton } from '@/components/ui/skeleton';
import { useShopsStore } from '@/stores/shops';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import type { ModelMerchantShopDirectoryShopV2 } from '@layup/api-sdk';
import BoostStoreModal from './boost-store-modal/BoostStoreModal.vue';
import DesktopFilterBar from './DesktopFilterBar.vue';
import FeaturedStoreCarousel from './FeaturedStoreCarousel.vue';
import MobileFilterBar from './MobileFilterBar.vue';
import ProductToggle from './ProductToggle.vue';
import SidebarFilters from './SidebarFilters.vue';
import StoreCard from './StoreCard.vue';

const shopsStore = useShopsStore();
const { shops, loading, error } = storeToRefs(shopsStore);

const PAGE_SIZE = 9;
const visibleCount = ref(PAGE_SIZE);
const visibleShops = computed(() => shops.value.slice(0, visibleCount.value));

watch(shops, () => {
  visibleCount.value = PAGE_SIZE;
});

const isDialogOpen = ref(false);
const selectedStoreId = ref('');

const openStore = (store: ModelMerchantShopDirectoryShopV2) => {
  if (shopsStore.currentProduct === 'boost') {
    selectedStoreId.value! = store.id!;
    isDialogOpen.value = true;
  } else {
    const domain = store.domain;
    if (!domain) return;
    window.open(domain.startsWith('http') ? domain : `https://${domain}`, '_blank');
  }
};
</script>
